import { useState } from 'react';
import { useFirestore } from '../../../firebase/useFirestore';
import { db } from '../../../firebase/config.js';
import useAutosaveField from '../../../hooks/useAutosaveField';
import Hostname from '../../../hooks/Hostname';
import AutosaveStatus from '../../common/AutosaveStatus';
import ToggleButton from '../../common/ToggleButton';
import Table from '../../common/table/Table.jsx';
import TableCell from '../../common/table/TableCell.jsx';
import resolveAppointmentKinds, { INZET_OPTIONS, newCustomKindKey } from '../../../helpers/tasks/resolveAppointmentKinds';

/** Ruim voor een eigen woord, krap genoeg voor een agendablok. */
const MAX_LABEL_LENGTH = 30;

/** Het veld op CompagnyMeta: een map per soortsleutel. */
const FIELD = 'AppointmentKinds';

/**
 * Instellingen → Ondersteuning → 'Soorten afspraak': de lijst waaruit de titel
 * van een afspraak gekozen wordt. FIXtool-organisaties beginnen met zeven
 * ingebouwde soorten (hernoemen en verbergen, niet weghalen); eigen soorten
 * komen erbij met 'Soort toevoegen'. De bovenste soort is de standaard voor
 * een nieuw huisbezoek. Per soort kies je ook waarvoor de uren tellen in de
 * SiSa/SPUK-verantwoording ('Telt als').
 *
 * Zelfde opzet als 'Labels bij huishoudens': verbergen en niet verwijderen,
 * want afspraken dragen de sleutel.
 * AI-CONTEXT: See docs/features/afspraak-soort-en-aanwezigheid-bkb.md
 *
 * @returns {JSX.Element}
 */
const AppointmentKindsConfig = () => {
    const [adding, setAdding] = useState(false);
    const compagny = useFirestore('CompagnyMeta');
    const { save, status } = useAutosaveField('CompagnyMeta');
    const host = Hostname();
    const brand = (host && host.Name) || '';

    const comp = Array.isArray(compagny) ? compagny[0] : undefined;
    const rows = comp ? resolveAppointmentKinds(comp, { brand, includeHidden: true }) : [];

    /** @param {string} key @param {string} value */
    const labelHandler = (key, value) => save(comp.docid, { [`${FIELD}.${key}.Label`]: value });

    // Meteen, niet na de wachttijd van het tekstveld: het menu is gestuurd door
    // het document, dus met wachten sprong het eerst terug naar de oude keuze.
    // De lokale schrijfactie komt direct terug in de snapshot.
    /** @param {string} key @param {string} value - 'uitvoering', 'advies' of '' */
    const inzetHandler = (key, value) => db.collection('CompagnyMeta').doc(comp.docid).update({ [`${FIELD}.${key}.Inzet`]: value });

    // Eén soort per klik, ook bij een dubbele klik: een soort is alleen te
    // verbergen, nooit te verwijderen, dus een tweede rij blijft voor altijd staan.
    // AI-CONTEXT: See docs/pitfalls.md#state-multi-step-signup-leaves-half-an-account
    const addKindHandler = async () => {
        if (adding) return;
        setAdding(true);
        const key = newCustomKindKey();
        const position = rows.filter((r) => !r.builtIn).length + 1;
        try {
            await db.collection('CompagnyMeta').doc(comp.docid).update({
                [`${FIELD}.${key}`]: { Label: 'Nieuwe soort', Position: position, Hidden: 'false' },
            });
        } finally {
            setAdding(false);
        }
    };

    return (
        <div className="dashboard-container">
            <h2>Soorten afspraak</h2>
            <p>
                Bij een afspraak kies je de titel uit deze lijst; met 'Anders…' typ je een eigen titel. De bovenste
                soort is de standaard voor een nieuw huisbezoek.
            </p>
            {!comp ? (
                <p className="status-config-loading">Instellingen worden geladen…</p>
            ) : (
                <>
                    <AutosaveStatus status={status} reserve />
                    {rows.length === 0 ? (
                        <p className="status-config-footnote">Nog geen soorten: de titel van een afspraak is vrije tekst.</p>
                    ) : (
                        <Table
                            columns={[
                                { key: 'label', label: 'NAAM', searchable: false },
                                { key: 'inzet', label: 'TELT ALS', searchable: false },
                                { key: 'hidden', label: 'VERBERGEN', searchable: false },
                            ]}
                            items={rows}
                            getItemKey={(row) => row.key}
                            showSearch={false}
                            tableClassName="status-config-table"
                            getRowClassName={(row) => (row.hidden ? 'adress-labels-config__row--hidden' : '')}
                            renderRow={(row) => (
                                <>
                                    <TableCell>
                                        {/* Ongecontroleerd veld met defaultValue, zie StatusConfigTable.
                                            AI-CONTEXT: See docs/pitfalls.md#ui-tabletitlecell-onchange-refocus */}
                                        <input
                                            type="text"
                                            maxLength={MAX_LABEL_LENGTH}
                                            defaultValue={row.label}
                                            onChange={(e) => labelHandler(row.key, e.target.value)}
                                            aria-label={`Naam van de soort '${row.label}'`}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {/* Gestuurd: de keuze staat in hetzelfde document als de rij.
                                            AI-CONTEXT: See docs/pitfalls.md#ui-select-defaultvalue-loses-late-options */}
                                        <select
                                            value={row.inzet}
                                            onChange={(e) => inzetHandler(row.key, e.target.value)}
                                            aria-label={`Telt als, soort '${row.label}'`}
                                        >
                                            {INZET_OPTIONS.map((option) => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </select>
                                    </TableCell>
                                    <TableCell>
                                        <ToggleButton
                                            docid={comp.docid}
                                            id={`${FIELD}.${row.key}.Hidden`}
                                            collection={'CompagnyMeta'}
                                            value={comp[FIELD]?.[row.key]?.Hidden}
                                        />
                                    </TableCell>
                                </>
                            )}
                        />
                    )}
                    <button type="button" className="adress-labels-config__add" onClick={addKindHandler} disabled={adding}>
                        + Soort toevoegen
                    </button>
                </>
            )}
        </div>
    );
};

export default AppointmentKindsConfig;
